import { D, ZERO, formatNumber, type DecimalInput } from '../decimal.js';
import { FormulaError } from '../formula/errors.js';
import { analyzeFormula, evaluateFormula, type Quantity, type Scope } from '../formula/evaluate.js';
import { defaultUnits, type UnitRegistry } from '../units.js';
import type { FamilyDefinition, FamilyOutput } from './families.js';

export type ParamValues = Readonly<Record<string, DecimalInput | null | undefined>>;

/** Couche de paramètres (du plus général au plus précis) : type, variante de niveau, instance. */
export interface ParamLayer {
  source: string;
  values?: ParamValues | null;
}

export interface LevelContext {
  code: string;
  name?: string;
  /** Hauteur d'étage réelle du niveau (m). */
  height?: DecimalInput | null;
}

export interface SegmentInput {
  label: string;
  length?: DecimalInput | null;
  params?: ParamValues | null;
}

export interface OpeningInput {
  code?: string;
  width: DecimalInput;
  height: DecimalInput;
  count: DecimalInput;
}

export interface ElementInput {
  family: FamilyDefinition;
  level: LevelContext;
  /** Nombre d'éléments identiques (mode simple « S1 × 8 ») ; 1 en mode détaillé. */
  quantity: DecimalInput;
  layers: ParamLayer[];
  segments?: SegmentInput[];
  openings?: OpeningInput[];
}

export interface ResolvedParam {
  key: string;
  label: string;
  unit: string;
  value: string | null;
  source: string;
}

export interface OutputValue {
  key: string;
  label: string;
  unit: string;
  kind: FamilyOutput['kind'] | undefined;
  primary: boolean;
  formula: string;
  /** Valeur décimale exacte (chaîne) ; absente en cas d'erreur. */
  value?: string;
  display?: string;
  steps?: string[];
  error?: { code: string; message: string };
}

export interface ElementResult {
  params: ResolvedParam[];
  outputs: OutputValue[];
  quantity: string;
  openingsArea: string;
  /** Paramètres requis non renseignés. */
  missing: string[];
  /** Description de la section pour la nomenclature (« 1,20 × 1,20 × 0,30 »). */
  section: string;
}

const isSet = (v: DecimalInput | null | undefined): v is DecimalInput => v !== null && v !== undefined && v !== '';

/**
 * Résolution des paramètres : défaut de la famille → hauteur du niveau → type → variante → instance.
 * La provenance de chaque valeur est conservée pour « Voir le calcul ».
 */
export function resolveParameters(family: FamilyDefinition, level: LevelContext, layers: ParamLayer[]): ResolvedParam[] {
  return family.parameters.map((p) => {
    let value: string | null = null;
    let source = 'à renseigner';
    if (isSet(p.default)) {
      value = D(p.default).toString();
      source = `famille ${family.name}`;
    }
    if (p.fromLevel === 'height' && isSet(level.height)) {
      value = D(level.height).toString();
      source = `hauteur du niveau ${level.code}`;
    }
    for (const layer of layers) {
      const v = layer.values?.[p.key];
      if (isSet(v)) {
        value = typeof v === 'string' ? v.trim().replace(',', '.') : D(v).toString();
        source = layer.source;
      }
    }
    return { key: p.key, label: p.label, unit: p.unit, value, source };
  });
}

function toScope(params: ResolvedParam[], extra: Record<string, Quantity>): Record<string, Quantity> {
  const scope: Record<string, Quantity> = {};
  for (const p of params) {
    const q: Quantity = { value: p.value, source: p.source, label: p.label };
    if (p.unit) q.unit = p.unit;
    scope[p.key] = q;
  }
  return { ...scope, ...extra };
}

function errorOf(error: unknown): { code: string; message: string } {
  if (error instanceof FormulaError) return { code: error.code, message: error.message };
  return { code: 'ERROR', message: error instanceof Error ? error.message : String(error) };
}

/**
 * Calcule toutes les sorties d'un élément (volume, coffrage…) avec leurs traces.
 * Une sortie en erreur (donnée manquante…) n'empêche pas le calcul des autres.
 */
export function computeElement(input: ElementInput, options: { units?: UnitRegistry } = {}): ElementResult {
  const units = options.units ?? defaultUnits;
  const { family } = input;
  const params = resolveParameters(family, input.level, input.layers);
  const quantity = D(input.quantity);

  let openingsArea = ZERO;
  for (const o of input.openings ?? []) openingsArea = openingsArea.plus(D(o.width).mul(o.height).mul(o.count));

  const reserved: Record<string, Quantity> = {
    n: { value: quantity, unit: 'U', source: 'nombre d\'éléments', label: 'Nombre' },
    S_ouv: {
      value: openingsArea,
      unit: 'm2',
      source: input.openings?.length ? `${input.openings.length} ouverture(s) : Σ largeur × hauteur × nombre` : 'aucune ouverture',
      label: 'Ouvertures déduites',
    },
  };

  const segments = family.segmentable ? (input.segments ?? []) : [];
  const lengthKey = family.segmentLengthKey ?? 'L';

  const outputs: OutputValue[] = family.outputs.map((out) => {
    const base: OutputValue = { key: out.key, label: out.label, unit: out.unit, kind: out.kind, primary: !!out.primary, formula: out.formula };
    try {
      if (!segments.length) {
        const r = evaluateFormula(out.formula, toScope(params, reserved), { outputUnit: out.unit, units });
        return { ...base, value: r.value.toString(), display: r.display, steps: r.steps };
      }
      // Section variable : chaque tronçon surcharge ses propres paramètres, puis Σ.
      let total = ZERO;
      const steps: string[] = [];
      let unitSymbol = '';
      for (const seg of segments) {
        const segParams = params.map((p) => {
          const own = p.key === lengthKey ? seg.length : seg.params?.[p.key];
          return isSet(own) ? { ...p, value: D(own).toString(), source: `tronçon ${seg.label}` } : p;
        });
        const r = evaluateFormula(out.formula, toScope(segParams, reserved), { outputUnit: out.unit, units });
        total = total.plus(r.value);
        unitSymbol = r.unitSymbol;
        steps.push(`${seg.label} : ${r.substituted} = ${r.display}`);
      }
      const decimals = units.resolve(out.unit).displayDecimals;
      const display = `${formatNumber(total, { maxDecimals: decimals })} ${unitSymbol}`.trim();
      steps.unshift(render(out.formula));
      steps.push(`Σ = ${display}`);
      return { ...base, value: total.toString(), display, steps };
    } catch (error) {
      return { ...base, error: errorOf(error) };
    }
  });

  const missing = params.filter((p) => p.value === null).map((p) => p.key);
  const sectionParams = family.parameters.filter((p) => p.section);
  const section = sectionParams
    .map((sp) => params.find((p) => p.key === sp.key)?.value)
    .map((v) => (v === null || v === undefined ? '?' : formatNumber(v, { minDecimals: 2, maxDecimals: 3 })))
    .join(' × ');

  return { params, outputs, quantity: quantity.toString(), openingsArea: openingsArea.toString(), missing, section };
}

function render(formula: string): string {
  return formula.replace(/\*/g, '×').replace(/\s+/g, ' ').trim();
}

/** Vérifie une définition de famille : formules analysables, unités cohérentes, identifiants connus. */
export function validateFamily(family: FamilyDefinition, options: { units?: UnitRegistry } = {}): Array<{ output: string; message: string }> {
  const units = options.units ?? defaultUnits;
  const problems: Array<{ output: string; message: string }> = [];
  const keys = new Set<string>();
  for (const p of family.parameters) {
    if (!/^[\p{L}_][\p{L}\p{N}_]*$/u.test(p.key)) problems.push({ output: p.key, message: `Nom de paramètre invalide « ${p.key} »` });
    if (p.key === 'n' || p.key === 'S_ouv') problems.push({ output: p.key, message: `« ${p.key} » est une variable réservée` });
    if (keys.has(p.key)) problems.push({ output: p.key, message: `Paramètre en double « ${p.key} »` });
    keys.add(p.key);
    if (p.unit) {
      try {
        units.resolve(p.unit);
      } catch {
        problems.push({ output: p.key, message: `Unité inconnue « ${p.unit} »` });
      }
    }
  }
  if (!family.outputs.length) problems.push({ output: '-', message: 'Au moins une sortie est requise' });
  const scope: Scope = Object.fromEntries([
    ...family.parameters.map((p) => [p.key, p.unit ? { value: null, unit: p.unit } : { value: null }]),
    ['n', { value: null, unit: 'U' }],
    ['S_ouv', { value: null, unit: 'm2' }],
  ]);
  for (const out of family.outputs) {
    try {
      const analysis = analyzeFormula(out.formula, scope, { outputUnit: out.unit, units });
      if (!analysis.ok) problems.push({ output: out.key, message: analysis.error?.message ?? 'Formule invalide' });
    } catch (error) {
      problems.push({ output: out.key, message: errorOf(error).message });
    }
  }
  return problems;
}

